import React, { useCallback, useEffect, useId, useRef } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQuery } from '@tanstack/react-query';
import Select from 'react-select';

import { AccessInformation as AccessInformationIcon } from '@/orderForm/OrderForm/OrderFormTitleIcons';
import API from '@/apiRequest';
import AddressSelector from './shared/AddressSelector';
import FieldErrorDisplay from './shared/FieldErrorDisplay';
import { sanitizeAddress } from './shared/sanitizeAddress';
import Radio from '@/components/common/elements/Radio';


const normalizeText = (value = '') =>
  value
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/gi, '')
    .trim()
    .toLowerCase();

const isRealEstateType = (value) => normalizeText(value) === 'na imobiliaria';
const isOtherType = (value) => normalizeText(value) === 'outro';
const shouldShowAddress = (value) => isRealEstateType(value) || isOtherType(value);

const customSelectStyles = (hasError, isDisabled) => ({
  control: (base, state) => ({
    ...base,
    minHeight: '37px',
    height: '37px',
    borderRadius: '4px',
    border: `1px solid ${hasError ? '#dc3545' : isDisabled ? '#ced4da' : state.isFocused ? '#80bdff' : '#ced4da'}`,
    boxShadow: state.isFocused && !hasError ? '0 0 0 0.2rem rgba(0,123,255,.25)' : 'none',
    fontFamily: 'Open Sans',
    fontSize: '14px',
    fontWeight: 'normal',
    backgroundColor: isDisabled ? '#efefef' : 'white',
    cursor: isDisabled ? 'not-allowed' : 'default',
    '&:hover': {
      borderColor: hasError ? '#dc3545' : isDisabled ? '#ced4da' : state.isFocused ? '#80bdff' : '#ced4da',
    },
  }),
  singleValue: base => ({
    ...base,
    fontWeight: 'normal',
    fontSize: '14px',
    color: isDisabled ? 'rgba(0,0,0,0.38)' : '#212529',
  }),
  placeholder: base => ({
    ...base,
    fontSize: '14px',
    fontWeight: 'normal',
    color: '#b1b1b1',
  }),
  option: (base, state) => ({
    ...base,
    fontSize: '14px',
    fontWeight: 'normal',
    fontFamily: 'Open Sans',
    backgroundColor: state.isSelected ? '#5882C1' : state.isFocused ? '#f0f4f9' : 'transparent',
    color: state.isSelected ? 'white' : '#374151',
    '&:active': { backgroundColor: '#5882C1' },
  }),
  valueContainer: base => ({
    ...base,
    padding: '0 12px',
    height: '100%',
  }),
  input: base => ({
    ...base,
    margin: 0,
    padding: 0,
    '& input': {
      height: '100% !important',
    },
  }),
  
  indicatorSeparator: () => ({ display: 'none' }),
});

const AccessInformationField = ({
  formData,
  onChange,
  schema,
  errorSchema,
  disabled,
  formContext,
  uiSchema,
  ...props
}) => {
  formData = formData || {};
  const intl = useIntl();
  const clientId = formContext?.clientId;
  const id = useId();
  const didInitializeExtraInstructions = useRef(false);

  const hasFieldError = (fieldName) => errorSchema?.[fieldName]?.__errors?.length > 0;

  const { data: clientAddresses = [] } = useQuery({
    queryKey: ['clientAddresses', clientId],
    queryFn: async () => {
      const { data } = await API.get(`/clients/${clientId}/addresses`);
      return Array.isArray(data?.data) ? data.data : [];
    },
    enabled: !!clientId,
  });

  // Default extraInstructions to false if undefined and property exists in schema
  useEffect(() => {
    if (!schema?.properties?.extraInstructions || didInitializeExtraInstructions.current) return;

    if (formData.extraInstructions === undefined) {
      didInitializeExtraInstructions.current = true;
      onChange({ ...formData, extraInstructions: false });
      return;
    }

    didInitializeExtraInstructions.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTypeChange = useCallback(
    (value) => {
      const updates = { type: value };

      if (!shouldShowAddress(value)) {
        updates.address = undefined;
        updates.realEstateAddress = undefined;
      }

      if (isRealEstateType(value) && clientAddresses?.length === 1) {
        updates.address = sanitizeAddress(clientAddresses[0].address);
        updates.realEstateAddress = clientAddresses[0].public_id;
      }

      onChange({ ...formData, ...updates });
    },
    [formData, onChange, clientAddresses]
  );

  const handleKeysDevolutionTypeChange = useCallback(
    (value) => {
      let updates = { keysDevolutionType: value };

      if (!shouldShowAddress(value)) {
        updates = {
          ...updates,
          keysDevolutionAddress: undefined,
          devolutionRealEstateAddress: undefined,
        };
      }

      if (isRealEstateType(value) && clientAddresses.length === 1 && !formData.keysDevolutionAddress?.city) {
        updates = {
          ...updates,
          keysDevolutionAddress: sanitizeAddress(clientAddresses[0].address),
          devolutionRealEstateAddress: clientAddresses[0].public_id,
        };
      }

      onChange({ ...formData, ...updates });
    },
    [formData, onChange, clientAddresses]
  );

  const handleRealEstateAddressChange = useCallback(
    (opt) => {
      if (!opt) {
        onChange({ ...formData, realEstateAddress: undefined, address: undefined });
        return;
      }
      const selected = clientAddresses.find((address) => address.public_id === opt.value);
      if (selected) {
        onChange({
          ...formData,
          realEstateAddress: opt.value,
          address: sanitizeAddress(selected.address),
        });
      }
    },
    [formData, onChange, clientAddresses]
  );

  const handleDevolutionRealEstateAddressChange = useCallback(
    (opt) => {
      if (!opt) {
        onChange({ ...formData, devolutionRealEstateAddress: undefined, keysDevolutionAddress: undefined });
        return;
      }
      const selected = clientAddresses.find((address) => address.public_id === opt.value);
      if (selected) {
        onChange({
          ...formData,
          devolutionRealEstateAddress: opt.value,
          keysDevolutionAddress: sanitizeAddress(selected.address),
        });
      }
    },
    [formData, onChange, clientAddresses]
  );

  const handleExtraInstructionsChange = useCallback(
    (value) => {
      const updates = { extraInstructions: value };
      if (!value) {
        updates.instructions = undefined;
      }
      onChange({ ...formData, ...updates });
    },
    [formData, onChange]
  );

  const typeOptions = schema?.properties?.type?.enum || [];
  const keysDevolutionTypeOptions = schema?.properties?.keysDevolutionType?.enum || [];

  const showPickupAddress = shouldShowAddress(formData.type);
  const showDevolutionAddress = shouldShowAddress(formData.keysDevolutionType);

  const titleKey = uiSchema?.['ui:title'] || 'accessInformation';

  return (
    <>
      <div className="mb-[0.6vw] mt-[0.6vw] flex items-center gap-2">
        <div className="flex items-center justify-center shrink-0 w-6 h-6 text-[#393939]">
          <AccessInformationIcon viewBox="0 0 34.016 34.016" className="w-full h-full fill-current object-contain" />
        </div>
        <span className="font-bold text-[#212529] font-['Open_Sans'] leading-tight" style={{ fontSize: '16px', fontWeight: 700 }}>
          <FormattedMessage id={titleKey} />
        </span>
      </div>

      <div className="row pl-2 sm:pl-4 md:pl-[34px]" style={{paddingLeft: '1vw', paddingRight: '1vw'}}>
        <div className="col-md-12 space-y-4">
        {/* Pickup Location - Modern Schema */}
        {schema?.properties?.type && (
          <div>
            <label className="mb-1 mt-[0.3vh] inline-block font-['Open_Sans'] text-[12px] font-bold text-[#212529]">
              <FormattedMessage id="keysLocationType" />
            </label>

            <div className="flex flex-wrap items-center gap-x-4">
              {typeOptions.map(option => (
                <div key={option} className="flex flex-row items-center gap-2 mb-2">
                  <Radio
                    name={`accessInformation_keysLocationType_${id}`}
                    id={`accessType-location-${option}`}
                    value={option}
                    checked={formData.type === option}
                    onChange={() => handleTypeChange(option)}
                    disabled={disabled}
                  />
                  <label 
                    htmlFor={`accessType-location-${option}`}
                    className="m-0 cursor-pointer font-['Open_Sans'] text-[14px] font-normal text-[#212529] leading-none"
                  >
                    <FormattedMessage id={option} />
                  </label>
                </div>
              ))}
            </div>

            <FieldErrorDisplay errorSchema={errorSchema} fieldName="type" />

            {showPickupAddress && (
              <div className="mt-3">
                {isRealEstateType(formData.type) && clientAddresses.length > 1 && (
                  <div className="mb-3 flex flex-col">
                    <label className="mb-1 mt-[0.3vh] inline-block font-['Open_Sans'] text-[12px] font-bold text-[#212529]">
                      <FormattedMessage id="realEstateForKeysLocation" />
                    </label>
                     <Select
                      options={clientAddresses.map(address => ({ label: address.name, value: address.public_id }))}
                      value={
                        formData.realEstateAddress
                          ? {
                              label: clientAddresses.find(address => address.public_id === formData.realEstateAddress)?.name,
                              value: formData.realEstateAddress,
                            }
                          : null
                      }
                      onChange={handleRealEstateAddressChange}
                      isDisabled={disabled}
                      placeholder={intl.formatMessage({ id: 'SELECT' })}
                      styles={customSelectStyles(hasFieldError('realEstateAddress'), disabled)}
                    />
                  </div>
                )}

                <AddressSelector
                  addressData={formData.address || {}}
                  onChange={(address) => onChange({ ...formData, address })}
                  disabled={disabled}
                  errors={errorSchema?.address}
                />
              </div>
            )}
          </div>
        )}

        {/* Devolution Location - Modern Schema */}
        {schema?.properties?.keysDevolutionType && (
          <div>
            <label className="mb-1 mt-[0.3vh] inline-block font-['Open_Sans'] text-[12px] font-bold text-[#212529]">
              <FormattedMessage id="keysDevolutionType" />
            </label>

            <div className="flex flex-wrap items-center gap-x-4">
              {keysDevolutionTypeOptions.map(option => (
                <div key={option} className="flex flex-row items-center gap-2 mb-2">
                  <Radio
                    name={`accessInformation_devolutionType_${id}`}
                    id={`devolutionType-location-${option}`}
                    value={option}
                    checked={formData.keysDevolutionType === option}
                    onChange={() => handleKeysDevolutionTypeChange(option)}
                    disabled={disabled}
                  />
                  <label 
                    htmlFor={`devolutionType-location-${option}`}
                    className="m-0 cursor-pointer font-['Open_Sans'] text-[14px] font-normal text-[#212529] leading-none"
                  >
                    <FormattedMessage id={option} />
                  </label>
                </div>
              ))}
            </div>

            <FieldErrorDisplay errorSchema={errorSchema} fieldName="keysDevolutionType" />

            {showDevolutionAddress && (
              <div className="mt-3">
                {isRealEstateType(formData.keysDevolutionType) && clientAddresses.length > 1 && (
                  <div className="mb-3 flex flex-col">
                    <label className="mb-1 mt-[0.3vh] inline-block font-['Open_Sans'] text-[12px] font-bold text-[#212529]">
                      <FormattedMessage id="devolutionRealEstateForKeysLocation" />
                    </label>
                     <Select
                      options={clientAddresses.map(address => ({ label: address.name, value: address.public_id }))}
                      value={
                        formData.devolutionRealEstateAddress
                          ? {
                              label: clientAddresses.find(address => address.public_id === formData.devolutionRealEstateAddress)?.name,
                              value: formData.devolutionRealEstateAddress,
                            }
                          : null
                      }
                      onChange={handleDevolutionRealEstateAddressChange}
                      isDisabled={disabled}
                      placeholder={intl.formatMessage({ id: 'SELECT' })}
                      styles={customSelectStyles(hasFieldError('devolutionRealEstateAddress'), disabled)}
                    />
                  </div>
                )}

                <AddressSelector
                  addressData={formData.keysDevolutionAddress || {}}
                  onChange={(address) => onChange({ ...formData, keysDevolutionAddress: address })}
                  disabled={disabled}
                  errors={errorSchema?.keysDevolutionAddress}
                />
              </div>
            )}
          </div>
        )}

        {/* Keys Location - Legacy Schema */}
        {schema?.properties?.keysLocation && (
          <div className="flex flex-col mb-3">
            <label className="mb-1 mt-[0.3vh] inline-block font-['Open_Sans'] text-[12px] font-bold text-[#212529]">
              <FormattedMessage id="keysLocation" />
            </label>
             <input
              type="text"
              value={formData.keysLocation || ''}
              placeholder={intl.formatMessage({ id: 'keysLocation' })}
              onChange={(e) => onChange({ ...formData, keysLocation: e.target.value })}
              disabled={disabled}
              className={`w-full rounded-[4px] !h-[37px] !px-[12px] py-[6px] font-['Open_Sans'] text-[14px] font-normal outline-none transition-all placeholder:text-gray-400 border-[1px] border-solid ${
                disabled ? 'bg-[#efefef] text-[rgba(0,0,0,0.38)] cursor-not-allowed' : 'bg-white text-[#919191]'
              } ${hasFieldError('keysLocation') ? 'border-[#dc3545] focus:border-[#dc3545]' : 'border-[#ced4da] focus:border-[#80bdff]'}`}
            />
            <FieldErrorDisplay errorSchema={errorSchema} fieldName="keysLocation" />
          </div>
        )}

        {/* Extra Instructions - Legacy Schema */}
        {schema?.properties?.extraInstructions && (
          <div className="mb-3">
            <label className="mb-1 mt-[0.3vh] inline-block font-['Open_Sans'] text-[12px] font-bold text-[#212529]">
              <FormattedMessage id="extraInstructions" />
            </label>
            <div className="flex flex-wrap items-center gap-x-4">
              <div className="flex flex-row items-center gap-2 mb-2">
                <Radio
                  name={`accessInformation_extraInstructions_${id}`}
                  id={`extraInstructions-no-${id}`}
                  value="false"
                  checked={formData.extraInstructions === false}
                  onChange={() => handleExtraInstructionsChange(false)}
                  disabled={disabled}
                />
                <label 
                  htmlFor={`extraInstructions-no-${id}`}
                  className="m-0 cursor-pointer font-['Open_Sans'] text-[14px] font-normal text-[#212529] leading-none"
                >
                  <FormattedMessage id="No" />
                </label>
              </div>
              <div className="flex flex-row items-center gap-2 mb-2">
                <Radio
                  name={`accessInformation_extraInstructions_${id}`}
                  id={`extraInstructions-yes-${id}`}
                  value="true"
                  checked={formData.extraInstructions === true}
                  onChange={() => handleExtraInstructionsChange(true)}
                  disabled={disabled}
                />
                <label 
                  htmlFor={`extraInstructions-yes-${id}`}
                  className="m-0 cursor-pointer font-['Open_Sans'] text-[14px] font-normal text-[#212529] leading-none"
                >
                   <FormattedMessage id="Yes" />
                </label>
              </div>
            </div>
            <FieldErrorDisplay errorSchema={errorSchema} fieldName="extraInstructions" />
          </div>
        )}

        {/* Instructions Text Area */}
        {schema?.properties?.instructions && (formData.extraInstructions === true || !schema?.properties?.extraInstructions) && (
          <div className="mt-2 flex flex-col">
            <label className="mb-1 mt-[0.3vh] inline-block font-['Open_Sans'] text-[12px] font-bold text-[#212529]">
              <FormattedMessage id="accessInformationInstructions" />
            </label>

             <textarea
              rows={2}
              value={formData?.instructions || ''}
              placeholder={intl.formatMessage({ id: 'ACCESS_INFO_INSTRUCTIONS_PLACEHOLDER' })}
              onChange={(event) => onChange({ ...formData, instructions: event.target.value })}
              disabled={disabled}
              className={`w-full rounded-[4px] !px-[12px] py-2 font-['Open_Sans'] text-[14px] font-normal outline-none transition-all placeholder:text-gray-400 border-[1px] border-solid ${
                disabled ? 'bg-[#efefef] text-[rgba(0,0,0,0.38)] cursor-not-allowed' : 'bg-white text-[#919191]'
              } ${hasFieldError('instructions') ? 'border-[#dc3545] focus:border-[#dc3545]' : 'border-[#ced4da] focus:border-[#80bdff]'}`}
            />
            <FieldErrorDisplay errorSchema={errorSchema} fieldName="instructions" />
          </div>
        )}
        </div>
      </div>
    </>
  );
};

AccessInformationField.propTypes = {
  formData: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  schema: PropTypes.object,
  errorSchema: PropTypes.object,
  disabled: PropTypes.bool,
  formContext: PropTypes.object,
  rootFormData: PropTypes.object,
  uiSchema: PropTypes.object,
};

export default AccessInformationField;
